import { useMutation, useQueryClient } from '@tanstack/react-query';

import commentReplyService from './commentReplyService';

import type { CreateCommentReplyParams } from './commentReplyService';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import {
    CommentReplyMutationKey,
    CommentReplyQueryKey,
    ReviewInstanceQueryKey,
} from '@/constants/QueryKeys';

export const useCreateCommentReply = (reviewInstanceId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [CommentReplyMutationKey.CREATE_COMMENT_REPLY],
        mutationFn: (params: CreateCommentReplyParams) =>
            commentReplyService.createCommentReply(params),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [CommentReplyQueryKey.COMMENT_REPLIES, variables.reviewQuestionAnswerId],
            });

            queryClient.setQueryData<ReviewInstance>(
                [ReviewInstanceQueryKey.REVIEW_INSTANCE, reviewInstanceId],
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        reviewAnswers: old.reviewAnswers?.map((reviewAnswer) => ({
                            ...reviewAnswer,
                            reviewQuestionAnswers: reviewAnswer.reviewQuestionAnswers.map(
                                (answer) => {
                                    if (answer.id === variables.reviewQuestionAnswerId) {
                                        return {
                                            ...answer,
                                            commentReplies: [
                                                ...(answer.commentReplies || []),
                                                data,
                                            ],
                                        };
                                    }
                                    return answer;
                                },
                            ),
                        })),
                    };
                },
            );
        },
    });
};
